import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_ANCHORED_COMMENT,
  CREATE_COMMENT,
  DELETE_COMMENT_THREAD,
  GET_COMMENT,
  GET_COMMENT_ANCHORS,
  GET_COMMENTS_FLAT,
  SOFT_REMOVE_COMMENT,
} from "../../../services/graphql/queriesMutations";
import type { CommentAnchorType } from "../../../services/types";
import type { AnchoredSelectionDraft } from "../../anchored-comments";
import type { CommentType } from "../../comments";

type CanonicalAnchoredCommentsArgs = {
  gistId: number | null | undefined;
  editId: number | null | undefined;
  enabled: boolean;
  user: any;
};

type CanonicalAnchoredCommentsState = {
  anchors: CommentAnchorType[];
  anchorsLoading: boolean;
  anchorsError?: unknown;
  comments: CommentType[];
  commentsLoading: boolean;
  commentsError?: unknown;
  currentUserId?: number;
  currentUserRole?: string;
  canCreateAnchoredComment: boolean;
  onCreateAnchoredComment?: (
    draft: AnchoredSelectionDraft,
    comment: string,
  ) => Promise<CommentAnchorType | void>;
  onReply?: (commentId: number, content: string) => Promise<void>;
  onSoftRemove?: (commentId: number) => Promise<void>;
  onHardDeleteThread?: (commentId: number) => Promise<void>;
};

function sortAnchorsByCreatedAtThenId(
  a: CommentAnchorType,
  b: CommentAnchorType,
): number {
  const aTime = Date.parse(a.createdAt || "");
  const bTime = Date.parse(b.createdAt || "");

  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
    return aTime - bTime;
  }

  return a.id - b.id;
}

function mergeCommentAnchorList(
  anchors: CommentAnchorType[],
  nextAnchor: CommentAnchorType,
): CommentAnchorType[] {
  const byId = new Map<number, CommentAnchorType>();
  anchors.forEach((anchor) => byId.set(anchor.id, anchor));
  byId.set(nextAnchor.id, nextAnchor);
  return Array.from(byId.values()).sort(sortAnchorsByCreatedAtThenId);
}

function normalizeComment(comment: any): CommentType | null {
  if (!comment || typeof comment.id !== "number") return null;

  const normalizedReplies = Array.isArray(comment.replies)
    ? comment.replies
        .map((reply: any) => normalizeComment(reply))
        .filter((reply: CommentType | null): reply is CommentType => Boolean(reply))
    : [];

  return {
    ...comment,
    comment: typeof comment.comment === "string" ? comment.comment : "",
    parentId: typeof comment.parentId === "number" ? comment.parentId : null,
    userId: typeof comment.userId === "number" ? comment.userId : 0,
    user: comment.user || {
      id: typeof comment.userId === "number" ? comment.userId : 0,
      name: "Anonymous",
    },
    createdAt: comment.createdAt || "",
    updatedAt: comment.updatedAt || comment.createdAt || "",
    replies: normalizedReplies,
  };
}

function normalizeCommentList(comments: any[]): CommentType[] {
  if (!Array.isArray(comments)) return [];
  return comments
    .map((comment) => normalizeComment(comment))
    .filter((comment: CommentType | null): comment is CommentType => Boolean(comment));
}

export function useCanonicalAnchoredComments({
  gistId,
  editId,
  enabled,
  user,
}: CanonicalAnchoredCommentsArgs): CanonicalAnchoredCommentsState {
  const hasGist = typeof gistId === "number" && Number.isFinite(gistId);
  const hasEdit = typeof editId === "number" && Number.isFinite(editId);
  const isReady = enabled && hasGist;
  const isAnchorReady = isReady && hasEdit;

  const {
    data: flatCommentsData,
    loading: flatCommentsLoading,
    error: flatCommentsError,
    refetch: refetchFlatComments,
  } = useQuery(GET_COMMENTS_FLAT, {
    variables: { gistId: gistId ?? 0 },
    skip: !isReady,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: legacyCommentsData,
    loading: legacyCommentsLoading,
    error: legacyCommentsError,
    refetch: refetchLegacyComments,
  } = useQuery(GET_COMMENT, {
    variables: { gistId: gistId ?? 0, editId: editId ?? null },
    skip: !isReady,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: anchorsData,
    loading: anchorsLoading,
    error: anchorsError,
    refetch: refetchAnchors,
  } = useQuery(GET_COMMENT_ANCHORS, {
    variables: {
      gistId: gistId ?? 0,
      editId: editId ?? null,
      status: "ACTIVE",
    },
    skip: !isAnchorReady,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [createComment] = useMutation(CREATE_COMMENT);
  const [createAnchoredComment] = useMutation(CREATE_ANCHORED_COMMENT);
  const [softRemoveComment] = useMutation(SOFT_REMOVE_COMMENT);
  const [deleteCommentThread] = useMutation(DELETE_COMMENT_THREAD);
  const [localAnchors, setLocalAnchors] = useState<CommentAnchorType[]>([]);

  useEffect(() => {
    setLocalAnchors([]);
  }, [gistId, editId]);

  const remoteAnchors = useMemo<CommentAnchorType[]>(
    () => (Array.isArray(anchorsData?.commentAnchors) ? anchorsData.commentAnchors : []),
    [anchorsData],
  );

  const anchors = useMemo(() => {
    const byId = new Map<number, CommentAnchorType>();
    remoteAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    localAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    return Array.from(byId.values()).sort(sortAnchorsByCreatedAtThenId);
  }, [localAnchors, remoteAnchors]);

  const comments = useMemo(() => {
    if (Array.isArray(flatCommentsData?.commentsFlat)) {
      return normalizeCommentList(flatCommentsData.commentsFlat);
    }

    if (Array.isArray(legacyCommentsData?.comments)) {
      return normalizeCommentList(legacyCommentsData.comments);
    }

    return [];
  }, [flatCommentsData, legacyCommentsData]);

  const commentsLoading = flatCommentsLoading || legacyCommentsLoading;
  const commentsError = flatCommentsError && legacyCommentsError ? flatCommentsError : undefined;
  const isCommentModerator = user?.userType === "admin" || user?.userType === "moderator";

  const refetchComments = useCallback(async () => {
    if (!isReady || !hasGist) return;

    try {
      await refetchFlatComments({ gistId: gistId ?? 0 });
    } catch (error) {
      console.warn(
        "Canonical anchored comments: flat comments refetch failed; legacy comments fallback remains available.",
        error,
      );
    }

    try {
      await refetchLegacyComments({ gistId: gistId ?? 0, editId: editId ?? null });
    } catch (error) {
      console.warn(
        "Canonical anchored comments: legacy comments refetch failed.",
        error,
      );
    }
  }, [editId, gistId, hasGist, isReady, refetchFlatComments, refetchLegacyComments]);

  const refetchAnchorList = useCallback(async () => {
    if (!isAnchorReady || !hasGist || !hasEdit) return;

    try {
      await refetchAnchors({
        gistId: gistId ?? 0,
        editId: editId ?? null,
        status: "ACTIVE",
      });
    } catch (error) {
      console.warn("Canonical anchored comments: anchor refetch failed.", error);
    }
  }, [editId, gistId, hasEdit, hasGist, isAnchorReady, refetchAnchors]);

  const handleReply = useCallback(
    async (parentId: number, content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      if (!user) {
        window.alert("Please login to reply to comments.");
        return;
      }

      if (!hasGist) {
        throw new Error("A Gist is required to reply to an anchored discussion.");
      }

      await createComment({
        variables: {
          newComment: {
            comment: trimmed,
            parentId,
            gistId,
            editId: editId ?? null,
          },
        },
      });

      await refetchComments();
      await refetchAnchorList();
    },
    [createComment, editId, gistId, hasGist, refetchAnchorList, refetchComments, user],
  );

  const handleSoftRemoveComment = useCallback(
    async (commentId: number) => {
      if (!user) {
        window.alert("Please login to remove comments.");
        return;
      }

      await softRemoveComment({ variables: { id: commentId } });
      await refetchComments();
      await refetchAnchorList();
    },
    [refetchAnchorList, refetchComments, softRemoveComment, user],
  );

  const handleHardDeleteThread = useCallback(
    async (commentId: number) => {
      if (!isCommentModerator) {
        window.alert("Only moderators and administrators can permanently delete comment threads.");
        return;
      }

      await deleteCommentThread({ variables: { id: commentId } });
      await refetchComments();
      await refetchAnchorList();
    },
    [deleteCommentThread, isCommentModerator, refetchAnchorList, refetchComments],
  );

  const handleCreateAnchoredComment = useCallback(
    async (draft: AnchoredSelectionDraft, comment: string) => {
      const trimmed = comment.trim();
      if (!trimmed) return;

      if (!user) {
        window.alert("Please login to create anchored comments.");
        return;
      }

      if (!hasGist || !hasEdit) {
        throw new Error("A Gist and its latest Edit are required to create an anchored discussion.");
      }

      const result = await createAnchoredComment({
        variables: {
          input: {
            gistId,
            editId,
            comment: trimmed,
            target: "BODY",
            selectedText: draft.selectedText,
            selectedHash: draft.selectedHash,
            startOffset: draft.startOffset,
            endOffset: draft.endOffset,
            blockIndex: draft.blockIndex,
            prefixText: draft.prefixText,
            suffixText: draft.suffixText,
            contextText: draft.contextText,
          },
        },
      });

      const createdAnchor = result.data?.createAnchoredComment as CommentAnchorType | undefined;
      if (createdAnchor) {
        setLocalAnchors((previousAnchors) => mergeCommentAnchorList(previousAnchors, createdAnchor));
      }

      await refetchComments();
      await refetchAnchorList();

      return createdAnchor;
    },
    [
      createAnchoredComment,
      editId,
      gistId,
      hasEdit,
      hasGist,
      refetchAnchorList,
      refetchComments,
      user,
    ],
  );

  return {
    anchors,
    anchorsLoading,
    anchorsError,
    comments,
    commentsLoading,
    commentsError,
    currentUserId: user?.id,
    currentUserRole: user?.userType,
    canCreateAnchoredComment: Boolean(user && isAnchorReady),
    onCreateAnchoredComment: user && isAnchorReady ? handleCreateAnchoredComment : undefined,
    onReply: user && isReady ? handleReply : undefined,
    onSoftRemove: user && isReady ? handleSoftRemoveComment : undefined,
    onHardDeleteThread: isCommentModerator && isReady ? handleHardDeleteThread : undefined,
  };
}
